/**
 * Authentication Context
 * Manages user authentication state and provides auth methods throughout the app
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/services/firebase';
import {
  signIn,
  signOutUser,
  registerUser,
  getUserDocument,
} from '@/services/auth';
import { User } from '@/types';
import { logger } from '@/utils/logger';
import { sessionManager } from '@/services/sessionManager';
import { initializeBaseConfigurations } from '@/services/initializeData';

interface AuthContextType {
  // Current user state
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;

  // Auth methods
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    displayName: string
  ) => Promise<void>;
  logout: () => Promise<void>;

  // User status checks
  isAuthenticated: boolean;
  isApproved: boolean;
  isPending: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Custom hook to use the auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        try {
          // Get user document from Firestore
          const userData = await getUserDocument(firebaseUser.uid);
          setUser(userData);

          if (userData) {
            logger.auth.login(userData.id, userData.email);

            // Initialize base configurations if user is approved
            if (userData.status === 'approved') {
              try {
                await initializeBaseConfigurations(userData.id);
              } catch (error) {
                logger.error(
                  'Failed to initialize base configurations:',
                  error
                );
              }
            }
          }
        } catch (error) {
          logger.error('Error fetching user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      const userCredential = await signIn(email, password);
      const userData = await getUserDocument(userCredential.user.uid);

      if (!userData) {
        throw new Error('No se encontraron datos del usuario');
      }

      if (userData.status === 'suspended') {
        await logout();
        throw new Error(
          'Su cuenta ha sido suspendida. Contacte al administrador.'
        );
      }

      setUser(userData);
      logger.auth.login(userData.id, userData.email);

      // Start session monitoring after successful login
      if (userData.status === 'approved') {
        sessionManager.startSession();

        // Initialize base configurations if they don't exist
        try {
          await initializeBaseConfigurations(userData.id);
        } catch (error) {
          logger.error('Failed to initialize base configurations:', error);
          // Don't fail login if configuration initialization fails
        }
      }
    } catch (error) {
      logger.auth.error('Login failed', error as Error);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    displayName: string
  ): Promise<void> => {
    try {
      setLoading(true);
      const userCredential = await registerUser(email, password, displayName);
      const userData = await getUserDocument(userCredential.user.uid);
      setUser(userData);
      logger.info('User registered successfully', {
        userId: userData?.id,
        email: userData?.email,
      });
    } catch (error) {
      logger.auth.error('Registration failed', error as Error);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Stop session monitoring
      sessionManager.stopSession();

      await signOutUser();
      setUser(null);
      setFirebaseUser(null);
      logger.auth.logout();
    } catch (error) {
      logger.auth.error('Logout failed', error as Error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!firebaseUser,
    isApproved: user?.status === 'approved',
    isPending: user?.status === 'pending',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
